import { FieldInfo } from '@/components/FieldInfo';
import type { OrgFormApi } from '@/config/organizations/formTypes';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import orgStyles from '../../styles.module.css';
import { stackProps } from '../constants';
import styles from '../styles.module.css';

type ContactFieldsProps = {
  form: OrgFormApi;
  isReadOnly?: boolean;
  section: 'primaryContact' | 'itContact';
};

// Type alias for the nested contact field paths TanStack Form supports
type ContactPath =
  | 'primaryContact.firstName'
  | 'primaryContact.lastName'
  | 'primaryContact.email'
  | 'primaryContact.phone'
  | 'itContact.firstName'
  | 'itContact.lastName'
  | 'itContact.email'
  | 'itContact.phone';

function ContactFieldsComponent({ form, isReadOnly = false, section }: ContactFieldsProps) {
  const { t } = useTranslation('orgs');

  const labelType = section === 'primaryContact' ? 'primary_contact' : 'it_contact';

  const sectionLabel = useMemo(
    () =>
      labelType === 'primary_contact' ? t('create_org_section_primary_contact') : t('create_org_section_it_contact'),
    [labelType, t],
  );

  return (
    <Stack {...stackProps} className={styles.contactDeetsContainer}>
      <Text as="h3" className={orgStyles.sectionHeading}>
        {sectionLabel}
      </Text>

      <Stack alignItems="start" gap={8}>
        {/* firstName */}
        <form.Field name={`${section}.firstName` as ContactPath}>
          {field => (
            <>
              <Input
                autoComplete="off"
                className={orgStyles.uiInput}
                data-testid={`input-${labelType}-first_name`}
                id={`first_name-${labelType}`}
                readOnly={isReadOnly}
                label={{
                  children: t('create_org_first_name_label'),
                  htmlFor: `first_name-${labelType}`,
                  id: `label-first_name-${labelType}`,
                }}
                name={`first_name-${labelType}`}
                value={field.state.value as string}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_org_first_name_placeholder')}
              />
              <FieldInfo field={field} />
            </>
          )}
        </form.Field>

        {/* lastName */}
        <form.Field name={`${section}.lastName` as ContactPath}>
          {field => (
            <>
              <Input
                autoComplete="off"
                className={orgStyles.uiInput}
                data-testid={`input-${labelType}-last_name`}
                id={`last_name-${labelType}`}
                readOnly={isReadOnly}
                label={{
                  children: t('create_org_last_name_label'),
                  htmlFor: `last_name-${labelType}`,
                  id: `label-last_name-${labelType}`,
                }}
                name={`last_name-${labelType}`}
                value={field.state.value as string}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_org_last_name_placeholder')}
              />
              <FieldInfo field={field} />
            </>
          )}
        </form.Field>

        {/* phone */}
        <form.Field name={`${section}.phone` as ContactPath}>
          {field => (
            <>
              <Input
                autoComplete="off"
                className={orgStyles.uiInput}
                data-testid={`input-${labelType}-phone`}
                id={`phone-${labelType}`}
                readOnly={isReadOnly}
                label={{
                  children: t('create_org_phone_label'),
                  htmlFor: `phone-${labelType}`,
                  id: `label-phone-${labelType}`,
                }}
                name={`phone-${labelType}`}
                value={(field.state.value as string) ?? ''}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_org_phone_placeholder')}
              />
              <FieldInfo field={field} />
            </>
          )}
        </form.Field>

        {/* email */}
        <form.Field name={`${section}.email` as ContactPath}>
          {field => (
            <>
              <Input
                autoComplete="off"
                className={orgStyles.uiInput}
                data-testid={`input-${labelType}-email`}
                id={`email-${labelType}`}
                readOnly={isReadOnly}
                label={{
                  children: t('create_org_email_label'),
                  htmlFor: `email-${labelType}`,
                  id: `label-email-${labelType}`,
                }}
                name={`email-${labelType}`}
                value={field.state.value as string}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_org_email_placeholder')}
                type="email"
              />
              <FieldInfo field={field} />
            </>
          )}
        </form.Field>
      </Stack>
    </Stack>
  );
}

export const ContactFields = memo(ContactFieldsComponent);
